import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';

/**
 * Collapses identical requests fired within a short window into one HTTP call.
 * Intended for reference data requested at the same time by a route resolver,
 * a page component and its children. Entries expire quickly, so later calls still hit the API.
 */
export class BurstRequestCache {
  private entries = new Map<string, { expiresAt: number; request$: Observable<any> }>();

  constructor(private ttlMs: number = 5000) { }

  get<T>(key: string, factory: () => Observable<T>): Observable<T> {
    const now = Date.now();
    const hit = this.entries.get(key);
    if (hit && hit.expiresAt > now) return hit.request$;

    const request$: Observable<T> = factory().pipe(
      catchError(error => {
        // Do not keep a failed request around; the next caller should retry.
        if (this.entries.get(key)?.request$ === request$) this.entries.delete(key);
        return throwError(error);
      }),
      shareReplay(1)
    );
    this.entries.set(key, { expiresAt: now + this.ttlMs, request$ });
    return request$;
  }

  clear(key?: string) {
    if (key) this.entries.delete(key);
    else this.entries.clear();
  }
}
